/**
 * AudioWaveformVisualization -- Shared audio player component with mirrored
 * waveform visualization, play/pause toggle, time display, playhead cursor,
 * and click-to-seek.
 *
 * Consolidates three separate audio playback implementations (AudioUploadNode,
 * SoundExpertNode, WaveformBar) into a single reusable component.
 *
 * Visual style: bars extend both above and below a center line (mirrored),
 * dense layout (~200 bars), thin bars (~1.5px), nearly continuous appearance.
 * Played region at full opacity, unplayed region at lower opacity.
 *
 * Playhead cursor: driven by requestAnimationFrame for 60fps smooth movement.
 * The cursor DOM element is updated via a ref (bypassing React render) to
 * avoid triggering 60 re-renders/second across all 200 waveform bars.
 *
 * Props:
 *   url          - audio URL (null = empty state, renders nothing)
 *   height       - waveform bar area height in px (default 80)
 *   barCount     - number of bars (default 200)
 *   accentColor  - bar + cursor color (default "#3b82f6" blue)
 *   className    - additional container classes
 *   compact      - compact mode: smaller play button + text
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useWaveform } from './AudioRemixShared'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AudioWaveformVisualizationProps {
  url: string | null
  height?: number
  barCount?: number
  accentColor?: string
  className?: string
  compact?: boolean
}

// ─── Utilities ──────────────────────────────────────────────────────────────

/** Format seconds as mm:ss */
function formatMMSS(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '00:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_HEIGHT = 80
const DEFAULT_BAR_COUNT = 200
const DEFAULT_ACCENT = '#3b82f6'

// ─── Component ──────────────────────────────────────────────────────────────

export const AudioWaveformVisualization = memo(function AudioWaveformVisualization({
  url,
  height = DEFAULT_HEIGHT,
  barCount = DEFAULT_BAR_COUNT,
  accentColor = DEFAULT_ACCENT,
  className,
  compact = false,
}: AudioWaveformVisualizationProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const rafIdRef = useRef<number>(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const bars = useWaveform(url, barCount)

  // Reset state when URL changes
  useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    // Reset cursor position directly
    if (cursorRef.current) {
      cursorRef.current.style.left = '0%'
      cursorRef.current.style.display = 'none'
    }
  }, [url])

  // ─── rAF loop for smooth cursor ────────────────────────────────────
  // Reads audioRef.current.currentTime every frame (~60fps) and updates
  // the cursor DOM element directly via ref. This bypasses React render
  // to avoid re-rendering 200 waveform bars 60 times/sec.
  useEffect(() => {
    if (!isPlaying) {
      // Cancel any running animation frame when paused
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
      return
    }

    const tick = () => {
      const el = audioRef.current
      const cursor = cursorRef.current
      if (el && cursor) {
        const dur = el.duration
        if (isFinite(dur) && dur > 0) {
          const progress = el.currentTime / dur
          cursor.style.left = `${progress * 100}%`
          cursor.style.display = progress > 0 ? '' : 'none'
        }
      }
      rafIdRef.current = requestAnimationFrame(tick)
    }

    rafIdRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
    }
  }, [isPlaying])

  const togglePlayback = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const el = audioRef.current
      if (!el) return
      if (isPlaying) {
        el.pause()
      } else {
        el.play()
      }
    },
    [isPlaying],
  )

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = audioRef.current
      if (!el || !duration) return
      const rect = e.currentTarget.getBoundingClientRect()
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      el.currentTime = fraction * duration
      setCurrentTime(el.currentTime)
      // Update cursor immediately via ref for instant visual feedback
      if (cursorRef.current) {
        cursorRef.current.style.left = `${fraction * 100}%`
        cursorRef.current.style.display = fraction > 0 ? '' : 'none'
      }
    },
    [duration],
  )

  const progress = duration > 0 ? currentTime / duration : 0
  const playedBars = Math.floor(progress * barCount)

  if (!url) return null

  // Size classes based on compact mode
  const btnSize = compact ? 'w-5 h-5' : 'w-7 h-7'
  const iconSize = compact ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'
  const textSize = compact ? 'text-[8px]' : 'text-xxs'

  return (
    <div className={className} data-testid="audio-waveform-visualization">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={() => {
          const el = audioRef.current
          if (el && isFinite(el.duration)) {
            setDuration(el.duration)
          }
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false)
          setCurrentTime(0)
          // Reset cursor on end
          if (cursorRef.current) {
            cursorRef.current.style.left = '0%'
            cursorRef.current.style.display = 'none'
          }
        }}
        onTimeUpdate={() => {
          const el = audioRef.current
          if (!el) return
          // Update time display state (throttled ~4fps by browser — fine for mm:ss text)
          setCurrentTime(el.currentTime)
          // Duration bug fix: for API-proxied audio, el.duration may be
          // Infinity at metadata-load time. Re-read once it becomes finite.
          if (duration === 0 && isFinite(el.duration) && el.duration > 0) {
            setDuration(el.duration)
          }
        }}
      />

      {/* Mirrored waveform area (click-to-seek) */}
      <div
        className="relative cursor-pointer"
        style={{ height }}
        onClick={handleSeek}
        data-testid="waveform-area"
      >
        {/* Bars — mirrored above and below center */}
        <div className="absolute inset-0 flex items-center gap-[0.5px]">
          {bars.length > 0
            ? bars.map((amp, i) => {
                const barHeight = Math.max(8, amp * 100)
                return (
                  <div
                    key={i}
                    data-testid="waveform-bar"
                    className="flex-1 rounded-[0.5px]"
                    style={{
                      height: `${barHeight}%`,
                      background: accentColor,
                      opacity: i < playedBars ? 0.8 : 0.35,
                      transition: i === playedBars ? 'opacity 100ms' : undefined,
                    }}
                  />
                )
              })
            : Array.from({ length: barCount }, (_, i) => (
                <div
                  key={i}
                  data-testid="waveform-bar"
                  className="flex-1 rounded-[0.5px]"
                  style={{
                    height: '8%',
                    background: accentColor,
                    opacity: 0.2,
                  }}
                />
              ))}
        </div>

        {/* Playhead cursor — positioned via rAF, no CSS transition needed */}
        <div
          ref={cursorRef}
          className="absolute top-0 bottom-0 w-[2px] pointer-events-none"
          style={{
            left: `${progress * 100}%`,
            background: accentColor,
            display: progress > 0 ? '' : 'none',
          }}
          data-testid="waveform-cursor"
        />
      </div>

      {/* Controls row: play/pause + time */}
      <div className="flex items-center gap-2 mt-1.5">
        <button
          onClick={togglePlayback}
          className={`${btnSize} rounded-full flex items-center justify-center transition-colors flex-shrink-0`}
          style={{
            background: `${accentColor}33`,
            border: `1px solid ${accentColor}66`,
          }}
          data-testid="play-pause-btn"
        >
          {isPlaying ? (
            <svg className={iconSize} fill={accentColor} viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg className={`${iconSize} ml-0.5`} fill={accentColor} viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        <span className={`${textSize} text-muted-foreground font-mono tabular-nums`}>
          {formatMMSS(currentTime)} / {formatMMSS(duration)}
        </span>
      </div>
    </div>
  )
})

AudioWaveformVisualization.displayName = 'AudioWaveformVisualization'
